import { useEffect, useState } from 'react';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { WishlistDetails } from '@lambda/apis/src/dynamoDb/types';
import cn from 'classnames';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import settings from '@/settings/configs';
import { ReviewSummaryT } from '@/settings/types';
import { Heart, X } from '@/public/icons/account/icons';
import { logger } from '@/lib/logger';
import { Breakpoints } from '@/settings/breakpoints';
import {
  trackAddToWishlist,
  trackRemoveFromWishlist,
} from '@/lib/analytics/trackProduct';
import useComparePrices from '@/lib/use-pim-compare-price';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import WishlistNotification from './WishlistNotification';
import { CustomerFormType } from '../commons/Customer/types';
import CognitoCustomerModal from '../commons/Customer/CognitoCustomerModal';

const BLACKLISTED_TAGS = [
  'express',
  'isBest',
  'isPaused',
  'marketPrice',
  'minPrice',
  'msrp',
  'psku',
  'rsku',
  'subsidy',
  'targetPrice',
  'email',
  'store',
  'image',
  'url',
  'urlOrigin',
  'urlPathname',
  'name',
  'price',
  'reviews',
  'timestamp',
  'deletedAt',
  'id',
  'title',
  'sku',
  'vendor',
  'touchbar',
  'version',
  'language',
  'charger',
];

const { store } = settings;

function generateDBKey(
  tags: string[],
  data: {
    image: string;
    url: string;
    urlOrigin: string;
    urlPathname: string;
    name: string;
    price: number;
    reviews: ReviewSummaryT;
    id: string;
    title: string;
    sku?: string;
    vendor: string;
  },
  email: string,
) {
  const formattedData = { ...extractTags(tags), ...data };

  const dbList = Object.keys(formattedData)
    .sort()
    .filter((key) => {
      if (!BLACKLISTED_TAGS.includes(key)) return key;

      return '';
    });

  const dbKey = dbList
    .filter(
      (key) =>
        typeof formattedData[key as keyof typeof formattedData] !== 'undefined',
    )
    .map((key) => formattedData[key as keyof typeof formattedData])
    .join('!');

  return `${email}!${store}!${dbKey}`;
}

type WishlistT = {
  isInWishlist?: boolean;
  listings: WishlistDetails[];
} | null;

enum WishlistOperation {
  ADD = 'add',
  REMOVE = 'remove',
}

export default function WishlistToggle({
  offer,
  reviews,
  oldPdp,
}: {
  offer: SearchOffers;
  reviews: ReviewSummaryT;
  oldPdp?: boolean;
}) {
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [wishlist, setWishlist] = useState<WishlistT>(null);
  const [shouldRemove, setShouldRemove] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const { customer } = useCustomerContext();

  const comparePrice = useComparePrices({
    psku: offer.psku,
    storage: offer.storage,
    tags: offer.tags,
    actualPrice: offer.price,
    fallbackPrice: offer.comparePrice,
  });

  // DB KEY LOGIC
  const { origin, pathname, href } = window.location;
  const shopifyId = `gid://shopify/ProductVariant/${offer.variantId}`;

  const data = {
    image: offer.imageSrc,
    url: href,
    urlOrigin: origin,
    urlPathname: pathname,
    name: offer.title,
    price: offer.price,
    reviews,
    id: shopifyId,
    title: offer.title,
    sku: offer.sku,
    vendor: offer.vendor,
  };
  const dbKey = generateDBKey(offer.tags, data, customer?.email || '');

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        if (customer?.email) {
          const res = await fetch(
            `/api/products/wishlistData?sk=${encodeURIComponent(dbKey)}`,
          );

          setWishlist(await res.json());
        } else {
          setWishlist(null);
        }
      } catch (err) {
        logger.error(err);
      }
    };

    fetchWishlist();
  }, [customer, dbKey]);

  if (wishlist == null && customer?.email) return null;

  const toggleWishlist = async (email: string, type: WishlistOperation) => {
    try {
      const result = await fetch('/api/products/wishlistData', {
        method: 'POST',
        body: JSON.stringify({
          ...extractTags(offer.tags),
          ...data,
          email,
          store: settings.store,
          deletedStatus: type,
        }),
      });

      if (result.status === 200) {
        const res: WishlistT = await result.json();

        setWishlist(res);
        setShowNotification(true);

        switch (type) {
          case WishlistOperation.ADD:
            trackAddToWishlist({
              offer,
              properties: {
                compare_price: comparePrice,
              },
            });
            break;
          case WishlistOperation.REMOVE:
            trackRemoveFromWishlist({
              offer,
              properties: {
                compare_price: comparePrice,
              },
            });
            break;
          default:
            logger.warn({ type, offer }, 'Unknown wishlist operation provided');
            break;
        }
      } else {
        throw new Error(
          `Failed to toggle wishlist. Status code: ${result.status}`,
        );
      }
    } catch (err) {
      logger.error(err);
    }
  };

  const addedToWishlist = customer?.email && wishlist && wishlist.isInWishlist;

  return (
    <>
      <button
        className={cn(
          'group flex h-[48px] w-full shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 px-3 leading-3',
          {
            'flex-col justify-center sm:flex-row sm:justify-start ': oldPdp,
            'flex-col justify-center lg:flex-row lg:justify-start': !oldPdp,
          },
        )}
        onClick={() => {
          if (!customer) {
            if (window.innerWidth > Breakpoints.MD) setShowCustomerModal(true);
            else setShowTooltip(!showTooltip);
          } else {
            setShouldRemove(false);
            toggleWishlist(
              customer.email,
              addedToWishlist
                ? WishlistOperation.REMOVE
                : WishlistOperation.ADD,
            );
          }
        }}
        onMouseEnter={() => addedToWishlist && setShouldRemove(true)}
        onMouseLeave={() => setShouldRemove(false)}
      >
        <Heart
          className={cn(
            'h-[18px] w-[18px] transition-all duration-300 ease-in-out',
            {
              'fill-gray-700 group-hover:fill-white': addedToWishlist,
              'group-hover:fill-gray-700': !addedToWishlist,
            },
          )}
          strokeWidth={1.5}
          stroke="#1f2323"
        />
        <div
          className={cn('-mt-1 text-center text-[10px]', {
            'sm:mt-0': oldPdp,
            'lg:mt-0': !oldPdp,
          })}
        >
          {shouldRemove ? (
            <span>Remove</span>
          ) : (
            <>
              <span className="mr-0.5">
                {addedToWishlist ? 'Added to List' : 'Add to List'}
              </span>
            </>
          )}
        </div>
      </button>
      {showTooltip && (
        <div className="relative">
          <div className="absolute right-0 top-1 z-40 mt-2 w-[250px] rounded-lg bg-gray-700 text-gray-700 shadow-md xs:top-[full] xs:w-[250px]">
            <div className="absolute -top-1 right-8 -z-10 h-3 w-3 rotate-45 bg-gray-700 shadow-md"></div>
            <div className="rounded-lg bg-gray-700 px-3 py-4 text-sm font-bold leading-tight text-white">
              <div className="pr-8 text-left">
                Get notified about deals and price drop for this product!
              </div>
              <button
                className="mt-3 w-full items-center rounded-lg bg-white p-2 py-3 text-black"
                onClick={() => {
                  setShowCustomerModal(true);
                }}
              >
                Sign up to receive deals
              </button>
            </div>
            <button
              onClick={() => {
                setShowTooltip(!showTooltip);
              }}
              type="button"
              className="absolute right-3 top-3"
              aria-label="Close"
            >
              <X className={cn('h-[18px] w-[18px] stroke-white')}></X>
            </button>
          </div>
        </div>
      )}
      <CognitoCustomerModal
        isOpen={showCustomerModal}
        formType={CustomerFormType.REGISTER}
        setIsOpen={setShowCustomerModal}
        additionalInfo="Create an account or log-in to get notified about deals and price drop for this product!"
      />
      {customer && (
        <WishlistNotification
          setShowNotification={setShowNotification}
          showNotification={showNotification}
          type={!wishlist?.isInWishlist ? 'removed' : 'added'}
          image={offer.imageSrc}
          addToWishlist={() => {
            toggleWishlist(customer.email, WishlistOperation.ADD);
          }}
        />
      )}
    </>
  );
}
